import React, { useState, useEffect, useCallback } from 'react';
import {
  getTierForPartner,
  tierSlug,
  resolveCardDisplay,
  logoSizePx,
  getInitials,
  storagePublicUrl,
} from '../../utils/partnerPublicDisplay';

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
  </svg>
);
const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="0.8" fill="currentColor" stroke="none" />
  </svg>
);
const YoutubeIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22.5 6.5s-.3-2-1.2-2.8C20.2 2.5 18.9 2.5 18.3 2.4 15.6 2.2 12 2.2 12 2.2s-3.6 0-6.3.2C5.1 2.5 3.8 2.5 2.7 3.7 1.8 4.5 1.5 6.5 1.5 6.5S1.3 8.8 1.3 11v2c0 2.3.2 4.5.2 4.5s.3 2 1.2 2.8c1.1 1.2 2.6 1.1 3.3 1.2C8.2 21.7 12 21.7 12 21.7s3.6 0 6.3-.2c.6-.1 1.9-.1 3-1.3.9-.8 1.2-2.8 1.2-2.8s.2-2.3.2-4.5v-2C22.7 8.8 22.5 6.5 22.5 6.5z" />
    <polygon points="10,8.5 15.5,12 10,15.5" fill="currentColor" stroke="none" />
  </svg>
);
const LinkIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
  </svg>
);
const GlobeIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
  </svg>
);
const MailIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <polyline points="2,4 12,13 22,4" />
  </svg>
);
const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.95 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012.86 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L7.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
  </svg>
);

const useInView = (threshold = 0.1, disabled = false) => {
  const [element, setElement] = useState(null);
  const [inView, setInView] = useState(disabled);
  const setRef = useCallback((node) => setElement(node), []);
  useEffect(() => {
    if (disabled) {
      setInView(true);
      return undefined;
    }
    if (!element) return undefined;
    const obs = new IntersectionObserver(
      ([e]) => { if (e?.isIntersecting) setInView(true); },
      { threshold, rootMargin: '100px 0px 0px 0px' }
    );
    obs.observe(element);
    return () => obs.disconnect();
  }, [element, threshold, disabled]);
  return [setRef, inView];
};

const SocialLinks = ({ company }) => {
  const links = [
    { path: company?.facebook_path, icon: <FacebookIcon />, label: 'Facebook' },
    { path: company?.ig_path, icon: <InstagramIcon />, label: 'Instagram' },
    { path: company?.youtube_path, icon: <YoutubeIcon />, label: 'YouTube' },
    { path: company?.other_path, icon: <LinkIcon />, label: 'Other' },
  ].filter((l) => l.path);
  if (!links.length) return null;
  return (
    <div className="social-row">
      {links.map((l, i) => (
        <a key={i} href={l.path} target="_blank" rel="noopener noreferrer" className="social-chip" title={l.label} aria-label={l.label}>
          {l.icon}
          <span className="sc-tooltip">{l.label}</span>
        </a>
      ))}
    </div>
  );
};

export const PartnerPublicPremiumCard = ({ partner, index = 0, staticPreview = false }) => {
  const tier = getTierForPartner(partner);
  const tierKey = tierSlug(partner);
  const display = resolveCardDisplay(partner);
  const logoPx = logoSizePx(display.logoSize, 'premium');
  const companyDescription = partner.company?.description || partner.notes || '-';
  const hasContact = Boolean(partner.company?.gmail || partner.company?.contact_number || partner.company?.website_link);
  const [cardRef, cardInView] = useInView(0.15, staticPreview);
  const visible = staticPreview || cardInView;

  return (
    <div
      ref={cardRef}
      className={`premium-card ${visible ? 'card-in' : ''}`}
      style={{
        '--tier-color': tier.color,
        '--tier-glow': tier.glow,
        animationDelay: `${index * 0.12}s`,
      }}
    >
      <div className="pc-top-line" />
      <div className="pc-corner pc-corner-tl" />
      <div className="pc-corner pc-corner-tr" />
      <div className="pc-corner pc-corner-bl" />
      <div className="pc-corner pc-corner-br" />
      <div className="pc-badge">
        <span className="pcb-label">{tier.label}</span>
      </div>
      {partner.company?.company_cover_picture ? (
        <div className="pc-cover" style={{ backgroundImage: `url(${storagePublicUrl(partner.company.company_cover_picture)})` }}>
          <div className="pc-cover-sweep" />
          <div className="pc-cover-overlay" />
        </div>
      ) : (
        <div className="pc-cover-placeholder">
          <div className="pcp-grid" />
        </div>
      )}
      <div className="pc-body">
        <div className="pc-tech-row">
          <span className="pc-tech-dot" />
          <span className="pc-tech-text">SPONSOR_NODE::{tierKey.toUpperCase()}</span>
        </div>
        <div className="pc-header">
          {display.logoSize !== 'hidden' && (
            <div className="pc-avatar" style={{ width: logoPx, height: logoPx }}>
              {partner.company?.company_logo ? (
                <img src={storagePublicUrl(partner.company.company_logo)} alt={partner.company?.name} className="pc-logo" />
              ) : (
                <span className="pc-initials">{getInitials(partner.company?.name)}</span>
              )}
              <div className="pc-avatar-ring" />
            </div>
          )}
          <div className="pc-info">
            <h3 className="pc-name">{partner.company?.name || 'Unknown Company'}</h3>
            <p className="pc-location">{partner.company?.location || '-'}</p>
          </div>
        </div>
        {display.isShowDescription && (
          <div className="pc-notes">
            <div className="pcn-bar" />
            <p>&quot;{companyDescription}&quot;</p>
          </div>
        )}
        {display.isShowContact && (
          <div className="pc-contacts">
            {partner.company?.gmail && (
              <a href={`mailto:${partner.company.gmail}`} className="pc-contact-row">
                <span className="pcc-icon"><MailIcon /></span>
                <span>{partner.company.gmail}</span>
              </a>
            )}
            {partner.company?.contact_number && (
              <div className="pc-contact-row">
                <span className="pcc-icon"><PhoneIcon /></span>
                <span>{partner.company.contact_number}</span>
              </div>
            )}
            {partner.company?.website_link && (
              <a href={partner.company.website_link} target="_blank" rel="noopener noreferrer" className="pc-contact-row link">
                <span className="pcc-icon"><GlobeIcon /></span>
                <span>Visit Website</span>
              </a>
            )}
            {!hasContact && (
              <div className="pc-contact-row">
                <span>-</span>
              </div>
            )}
          </div>
        )}
        <SocialLinks company={partner.company} />
      </div>
      <div className="pc-bottom-line" />
    </div>
  );
};

export const PartnerPublicRegularCard = ({ partner, index = 0, staticPreview = false }) => {
  const tier = getTierForPartner(partner);
  const display = resolveCardDisplay(partner);
  const logoPx = logoSizePx(display.logoSize, 'regular');
  const companyDescription = partner.company?.description || partner.notes || '-';
  const hasContact = Boolean(partner.company?.gmail || partner.company?.contact_number || partner.company?.website_link);
  const [ref, inView] = useInView(0.1, staticPreview);
  const visible = staticPreview || inView;

  return (
    <div
      ref={ref}
      className={`reg-card ${visible ? 'card-in' : ''}`}
      style={{ '--tier-color': tier.color, '--tier-glow': tier.glow, animationDelay: `${index * 0.08}s` }}
    >
      <div className="rc-top-line" />
      <div className="rc-grid-overlay" />
      <div className="rc-badge">
        <span className="rcb-dot" />
        <span>{tier.label}</span>
      </div>
      <div className="rc-body">
        <div className="rc-tech-row">
          <span className="rc-tech-dot" />
          <span className="rc-tech-text">LINK_ESTABLISHED</span>
        </div>
        <div className="rc-header">
          {display.logoSize !== 'hidden' && (
            <div className="rc-avatar" style={{ width: logoPx, height: logoPx }}>
              {partner.company?.company_logo ? (
                <img src={storagePublicUrl(partner.company.company_logo)} alt={partner.company?.name} className="rc-logo" />
              ) : (
                <span className="rc-initials">{getInitials(partner.company?.name)}</span>
              )}
            </div>
          )}
          <div className="rc-info">
            <h4 className="rc-name">{partner.company?.name || 'Unknown Company'}</h4>
            <p className="rc-location">{partner.company?.location || '-'}</p>
          </div>
        </div>
        {display.isShowDescription && <p className="rc-desc">{companyDescription}</p>}
        {display.isShowContact && (
          <div className="rc-contacts">
            {partner.company?.gmail && (
              <a href={`mailto:${partner.company.gmail}`} className="rc-contact">
                <MailIcon /><span>{partner.company.gmail}</span>
              </a>
            )}
            {partner.company?.website_link && (
              <a href={partner.company.website_link} target="_blank" rel="noopener noreferrer" className="rc-contact link">
                <GlobeIcon /><span>Visit Website</span>
              </a>
            )}
            {!hasContact && (
              <div className="rc-contact">
                <span>-</span>
              </div>
            )}
          </div>
        )}
        <SocialLinks company={partner.company} />
      </div>
      <div className="rc-bottom-line" />
    </div>
  );
};

/** Renders premium or regular card for a partner. */
export function PartnerPublicCard({ partner, layout = 'regular', index = 0, staticPreview = false }) {
  if (layout === 'premium') {
    return <PartnerPublicPremiumCard partner={partner} index={index} staticPreview={staticPreview} />;
  }
  return <PartnerPublicRegularCard partner={partner} index={index} staticPreview={staticPreview} />;
}
